import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { NewsModePersonalNews } from '@app/common/news-mode';
import { PageQuery } from '@app/common/page-query';
import { PageType } from '@app/models/page';
import { SearchPostQuery } from '@app/models/search-post-query';
import { SearchResult } from '@app/models/search-result';
import { SearchSuggestion } from '@app/models/search-suggestion';
import { SharePointJsonSearchResult } from '@app/models/sharepoint-json-search-result';
import { CacheService } from '@app/services/cache.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { Environment } from '@environments/interface.environment';
import { addMinutes, parseISO } from 'date-fns';
import { defer, EMPTY, iif, Observable, of } from 'rxjs';
import { catchError, expand, map, shareReplay, switchMap, tap, toArray } from 'rxjs/operators';
import { LicenseService } from './license.service';

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  constructor(private http: HttpClient,
              @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private httpHelper: HttpHelper,
              private cache: CacheService,
              private licenseService: LicenseService) { }

  public readonly getAllSiteCollectionsCacheKey = 'SearchService-getAllSiteCollections';
  public readonly getAllHubConnectedContentPagesCacheKey = 'SearchService-getAllHubConnectedContentPages';
  public readonly getAllModernNewsPagesCacheKey = 'SearchService-getAllModernNewsPages';

  public useCache = true;
  private getAllSiteCollections$: Observable<Array<SearchResult>>;
  private getAllHubConnectedContentPages$ = new Map<string, Observable<Array<SearchResult>>>();
  private getAllModernNewsPages$: Observable<Array<SearchResult>>;
  private disableLocalStorage = this.featureSettingsStoreService.state.disableLocalStorage ? this.featureSettingsStoreService.state.disableLocalStorage : false;

  getSuggestions(query: string, sourceId: string = '8413cd39-2156-4e00-b54d-11efd9abdb89', amountOfSuggestions: number = 5): Observable<SearchSuggestion> {
    const httpParams = new HttpParams()
      .append('querytext', `'${this.escapeQueryTextForSharePoint(query)}'`)
      .append('showpeoplenamesuggestions', `true`)
      .append('fprequerysuggestions', `true`)
      .append('enablestemming', `true`)
      .append('fprefixmatchallterms', `false`)
      .append('clienttype', `'custom'`)
      .append('fhithighlighting', `false`)
      .append('sourceid', `'${sourceId}'`)
      .append('inumberofquerysuggestions', `${amountOfSuggestions}`)
      .append('inumberofresultsuggestions', `${amountOfSuggestions}`)
      .append('inumberofpopularresultsuggestions', `${amountOfSuggestions}`)
      .append('fcapitalizefirstletters', `true`);
    return this.http.get<SearchSuggestion>(`${this.environment.coreWorkplaceSiteUrl}/_api/search/suggest`, { params: httpParams });
  }

  recordPageClick(searchResult: SearchResult) {

    const httpParams = new HttpParams()
      .append('pageInfo', `'${searchResult.piPageImpression}'`)
      .append('clickType', `'Result'`)
      .append('blockType', `${searchResult.piPageImpressionBlockType}`)
      .append('clickedResultId', `'${searchResult.piSearchResultId}'`)
      .append('subResultIndex', `${0}`)
      .append('immediacySourceId', `'${searchResult.SourceId}'`)
      .append('immediacyQueryString', `'${searchResult.SearchTerm}'`)
      .append('immediacyTitle', `'${searchResult.Title || searchResult.PreferredName}'`)
      .append('immediacyUrl', `'${searchResult.Path}'`);

    return this.http.get<any>(`${this.environment.coreWorkplaceSiteUrl}/_api/search/recordpageclick`, { params: httpParams });
  }

  getPeopleWithBirthdayDate(dateToQuery, rowLimit): Observable<Array<SearchResult>> {

    // Set birthday filter to today
    const refinementFilters = `'${this.featureSettingsStoreService.state.managedPropertyConfig.birthDayDateManagedProperty}:${dateToQuery}'`;
    const birthdaysQuery = '*';
    const birthdayOtherParams = new Map<string, string>();

    birthdayOtherParams.set('sourceid', `'` + this.featureSettingsStoreService.state.peopleResultSourceId + `'`); // local people results
    birthdayOtherParams.set('selectproperties', `'AccountName,FirstName,LastName,PreferredName,JobTitle,Department,Path,WorkEmail,PictureURL,${this.featureSettingsStoreService.state.managedPropertyConfig.birthDayDateManagedProperty}'`);
    birthdayOtherParams.set('refinementfilters', refinementFilters);
    birthdayOtherParams.set('sortlist', `'FirstName:ascending'`);

    return this.executeSearch(birthdaysQuery, rowLimit, false, birthdayOtherParams);
  }

  /** Generic search functionality */
  getResults(query: string, rowLimit?: number, includeOneDriveFiles?: boolean, selectProperties?: string[]): Observable<Array<SearchResult>> {
    const otherParams = new Map<string, string>();
    if (selectProperties) {
      otherParams.set('selectproperties', `'${selectProperties.join(',')}'`);
    }
    return this.executeSearch(this.escapeQueryTextForSharePoint(query), rowLimit, includeOneDriveFiles, otherParams);
  }

  /** Search for people */
  getPeople(query: string, rowLimit?: number): Observable<Array<SearchResult>> {
    const otherParams = new Map<string, string>();
    otherParams.set('sourceid', `'` + this.featureSettingsStoreService.state.peopleResultSourceId + `'`);
    // In case we use a custom result source the properties have to be explicitly requested
    otherParams.set('selectproperties', `'AccountName,FirstName,LastName,PreferredName,JobTitle,Department,Path,WorkEmail,PictureURL,${this.featureSettingsStoreService.state.managedPropertyConfig.birthDayDateManagedProperty}'`);
    return this.executeSearch(this.escapeQueryTextForSharePoint(query), rowLimit, false, otherParams);
  }

  /** Search for documents */
  getDocuments(query: string, fileTypes?: string[], rowLimit?: number, includeOneDriveFiles?: boolean, selectProperties?: string[], hubSiteId?: string, includeNewsSiteContentIfHubSiteQuery?: boolean): Observable<Array<SearchResult>> {

    let hubSiteQuery = '';
    if (hubSiteId) {
      if (includeNewsSiteContentIfHubSiteQuery === true) {
        hubSiteQuery = ` AND ( DepartmentId:{${hubSiteId}} OR Path:${this.featureSettingsStoreService.state.newsCenterSiteUrl}) `;
      } else {
        hubSiteQuery = ` AND DepartmentId:{${hubSiteId}} `;
      }
    }

    const documentsQuery = this.escapeQueryTextForSharePoint(query) + hubSiteQuery + ' AND isDocument=true';
    const otherParams = new Map<string, string>();
    if (selectProperties) {
      selectProperties.push('contentclass');
      otherParams.set('selectproperties', `'${selectProperties.join(',')}'`);
    }
    otherParams.set('refinementfilters', `'or(ProgID:OneNote.Notebook,fileExtension:or(${fileTypes.join(', ')}))'`);
    return this.executeSearch(documentsQuery, rowLimit, includeOneDriveFiles, otherParams, true, true);
  }

  /** Search for documents */
  getGuideDocuments(query: string, fileTypes?: string[], rowLimit?: number, selectProperties?: string[]): Observable<Array<SearchResult>> {
    const documentsQuery = this.escapeQueryTextForSharePoint(query) + ` AND Path:${this.featureSettingsStoreService.state.guideSiteUrl}`;
    const otherParams = new Map<string, string>();
    if (selectProperties) {
      otherParams.set('selectproperties', `'${selectProperties.join(',')}'`);
    }
    otherParams.set('refinementfilters', `'or(ProgID:OneNote.Notebook,fileExtension:or(${fileTypes.join(', ')}))'`);
    return this.executeSearch(documentsQuery, rowLimit, false, otherParams, true);
  }

  /** Search for pages */
  getGuidePages(query: string, rowLimit?: number, includeOneDriveFiles?: boolean, selectProperties?: string[]): Observable<Array<SearchResult>> {

    const pagesQuery = this.escapeQueryTextForSharePoint(query)
      + ` AND contentclass:STS_ListItem_WebPageLibrary AND Path:${this.featureSettingsStoreService.state.guideSiteUrl} AND ((ContentTypeId:0x0101009D1CB255DA76424F860D91F20E6C4118* AND PromotedState:0) OR (ContentTypeId:0x010100C568DB52D9D0A14D9B2FDCC96666E9F2007948130EC3DB064584E219954237AF39*))`;

    const otherParams = new Map<string, string>();
    if (selectProperties) {
      if (this.featureSettingsStoreService.state.showDescriptionInSearchResult) {
        selectProperties.push('Description');
      }
      otherParams.set('selectproperties', `'${selectProperties.join(',')}'`);
    }
    return this.executeSearch(pagesQuery, rowLimit, includeOneDriveFiles, otherParams);
  }

  /** Search for site collections */
  getSites(query: string, rowlimit?: number): Observable<Array<SearchResult>> {
    const sitesQuery = this.escapeQueryTextForSharePoint(query) + ' AND contentclass:sts_site';
    return this.executeSearch(sitesQuery, rowlimit, false);
  }

  /** Get all sites connected to a hub site */
  getHubSiteSites(hubSiteId: string, rowlimit?: number): Observable<Array<SearchResult>> {
    const sitesQuery = `contentclass:sts_site AND DepartmentId={${hubSiteId}}`;
    return this.executeSearch(sitesQuery, rowlimit, false);
  }

  /** Search for all pages connected to the hubsite */
  getAllHubConnectedContentPages(hubSiteId: string): Observable<Array<SearchResult>> {
    const cacheKey = `${this.getAllHubConnectedContentPagesCacheKey}-getAllHubConnectedContentPages-${hubSiteId}`;

    if (this.getAllHubConnectedContentPages$.has(cacheKey)) {
      return this.getAllHubConnectedContentPages$.get(cacheKey);
    } else {
      const sitesQuery = `PromotedState=0 AND DepartmentId={${hubSiteId}}`;
      const otherParams = new Map<string, string>();
      otherParams.set('selectproperties', `'Title,Path,Created,LastModifiedTime,SiteName,SiteTitle,${this.environment.featureSettings.managedPropertyConfig.newsLanguageManagedProperty}'`);
      this.getAllHubConnectedContentPages$.set(cacheKey, this.executeRecursiveSPSearch(sitesQuery, 500, 0, otherParams, false)
        .pipe(
          tap(data => {
            // Cache for 15 min and then return only return the cached value if available since this is an expensive request
            this.useCache && !this.disableLocalStorage && this.cache.set(cacheKey, data, addMinutes(new Date(), 15));
          }),
          shareReplay({ bufferSize: 1, refCount: true })
        ));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<SearchResult>>(this.getAllHubConnectedContentPagesCacheKey);
        if (cachedData) {
          return of(cachedData);
        }
      }
      return this.getAllHubConnectedContentPages$.get(cacheKey);
    }
  }

  /** Search for all modern newspages connected to the newscenter site if NewsModePersonalNews.GraphFromNewsCenterSite is set, otherwise all */
  getAllModernNewsPagesFromNewsCenter(): Observable<Array<SearchResult>> {
    if (this.getAllModernNewsPages$) {
      return this.getAllModernNewsPages$;
    } else {
      let sitesQuery = `ContentTypeId:0x0101009D1CB255DA76424F860D91F20E6C4118* AND PromotedState=2`;
      if (this.featureSettingsStoreService.state.newsModePersonalNews === NewsModePersonalNews.GraphFromNewsCenterSite) {
        sitesQuery += ` AND (SiteName:${this.featureSettingsStoreService.state.newsCenterSiteUrl})`;
      }
      const otherParams = new Map<string, string>();
      otherParams.set('selectproperties', `'Title,Path,Created,LastModifiedTime,FirstPublishedDate,SiteName,SiteTitle,${this.environment.featureSettings.managedPropertyConfig.newsLanguageManagedProperty}'`);
      this.getAllModernNewsPages$ = this.executeRecursiveSPSearch(sitesQuery, 500, 0, otherParams, false).pipe(
        tap(data => {
          // Cache for 15 min and then return only return the cached value if available since this is an expensive request
          this.useCache && !this.disableLocalStorage && this.cache.set(this.getAllModernNewsPagesCacheKey, data, addMinutes(new Date(), 15));
        }),
        shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<SearchResult>>(this.getAllModernNewsPagesCacheKey);
        if (cachedData) {
          return of(cachedData);
        }
      }
      return this.getAllModernNewsPages$;
    }
  }

  /** Fetch all site collections */
  getAllSiteCollections(): Observable<Array<SearchResult>> {
    if (this.getAllSiteCollections$) {
      return this.getAllSiteCollections$;
    } else {
      const sitesQuery = '-WebTemplate:SPSPERS -SiteId:null -WebId:null contentclass=STS_Site';
      const otherParams = new Map<string, string>();
      otherParams.set('selectproperties', `'Title,Path,GroupId'`);

      this.getAllSiteCollections$ = this.executeRecursiveSPSearch(sitesQuery, 500, 0, otherParams, false)
        .pipe(
          tap(data => {
            // Cache for 15 min and then return only return the cached value if available since this is an expensive request
            this.useCache && this.cache.set(this.getAllSiteCollectionsCacheKey, data, addMinutes(new Date(), 15));
          }),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<SearchResult>>(this.getAllSiteCollectionsCacheKey);
        if (cachedData) {
          return of(cachedData);
        }
      }
      return this.getAllSiteCollections$;
    }
  }

  /** Search for modern news */
  getModernNewsPages(query: string, rowLimit?: number, includeOneDriveFiles?: boolean, selectProperties?: string[], hubSiteId?: string, includeNewsSiteContentIfHubSiteQuery?: boolean): Observable<Array<SearchResult>> {

    let hubSiteQuery = '';
    if (hubSiteId) {
      if (includeNewsSiteContentIfHubSiteQuery === true) {
        hubSiteQuery = ` AND ( DepartmentId:{${hubSiteId}} OR Path:${this.featureSettingsStoreService.state.newsCenterSiteUrl}) `;
      } else {
        hubSiteQuery = ` AND DepartmentId:{${hubSiteId}} `;
      }
    }

    const newsQuery = this.escapeQueryTextForSharePoint(query)
      + ' AND contentclass:STS_ListItem_WebPageLibrary AND ContentTypeId:0x0101009D1CB255DA76424F860D91F20E6C4118* AND PromotedState:2'
      + hubSiteQuery;

    const otherParams = new Map<string, string>();
    if (selectProperties) {
      if (this.featureSettingsStoreService.state.showDescriptionInSearchResult) {
        selectProperties.push('Description');
      }
      selectProperties.push('contentclass');
      otherParams.set('selectproperties', `'${selectProperties.join(',')}'`);
    }

    otherParams.set('hithighlightedproperties', `'Title'`);

    return this.executeSearch(newsQuery, rowLimit, includeOneDriveFiles, otherParams, true, true);
  }

  /** Search for pages */
  getPages(query: string, rowLimit?: number, includeOneDriveFiles?: boolean, selectProperties?: string[], hubSiteId?: string, includeNewsSiteContentIfHubSiteQuery?: boolean): Observable<Array<SearchResult>> {

    let hubSiteQuery = '';
    if (hubSiteId) {
      if (includeNewsSiteContentIfHubSiteQuery === true) {
        hubSiteQuery = ` AND ( DepartmentId:{${hubSiteId}} OR Path:${this.featureSettingsStoreService.state.newsCenterSiteUrl}) `;
      } else {
        hubSiteQuery = ` AND DepartmentId:{${hubSiteId}} `;
      }
    }

    const pagesQuery = this.escapeQueryTextForSharePoint(query)
      + ' AND contentclass:STS_ListItem_WebPageLibrary AND ((ContentTypeId:0x0101009D1CB255DA76424F860D91F20E6C4118* AND PromotedState:0) OR (ContentTypeId:0x010100C568DB52D9D0A14D9B2FDCC96666E9F2007948130EC3DB064584E219954237AF39*))'
      + hubSiteQuery;
    const otherParams = new Map<string, string>();
    if (selectProperties) {
      if (this.featureSettingsStoreService.state.showDescriptionInSearchResult) {
        selectProperties.push('Description');
      }
      selectProperties.push('contentclass');
      otherParams.set('selectproperties', `'${selectProperties.join(',')}'`);
    }
    return this.executeSearch(pagesQuery, rowLimit, includeOneDriveFiles, otherParams, true, true);
  }

  /** Finds pages matching the PageQuery */
  getModernPagesByPageQuery(query: PageQuery) {
    let queryText = query.FindText ? '"' + this.escapeQueryTextForSharePoint(query.FindText) + '*"' + ' AND ' : '';
    queryText += `contentclass:STS_ListItem_WebPageLibrary AND ContentTypeId:0x0101009D1CB255DA76424F860D91F20E6C4118*` + ' AND ' + `PromotedState=${query.PageType === PageType.News ? 2 : 0}`;
    if (query.PageType === PageType.News && this.featureSettingsStoreService.state.newsModePersonalNews === NewsModePersonalNews.GraphFromNewsCenterSite) {
      queryText += ' AND ' + `Path:${this.featureSettingsStoreService.state.newsCenterSiteUrl}`;
    }
    if (query.StartDate) {
      queryText += ' AND ' + (query.PageType === PageType.News ? `FirstPublishedDate>=${query.StartDate.toISOString()}` : `Created>=${query.StartDate.toISOString()}`);
    }
    if (query.EndDate) {
      queryText += ' AND ' + (query.PageType === PageType.News ? `FirstPublishedDate<=${query.EndDate.toISOString()}` : `Created<=${query.EndDate.toISOString()}`);
    }
    if (query.Featured) {
      queryText += ' AND ' + `${this.environment.featureSettings.managedPropertyConfig.newsIsTopNewsManagedProperty}:1`;
    }
    if (query.OwnerOrEditorUpnId) {
      queryText += ' AND ' + `(ModifiedById:${query.OwnerOrEditorUpnId}* OR AuthorOWSUSER:${query.OwnerOrEditorUpnId}*)`;
    }
    if (query.hubSiteId) {
      queryText += ' AND ' + `DepartmentId:{${query.hubSiteId}}`;
    }
    if (this.featureSettingsStoreService.state.newsArchiveFilter) {
      if (query.IncludeArchive) {
        queryText += ' AND ' + `${this.environment.featureSettings.managedPropertyConfig.newsIsArchivedManagedProperty}:1`;
      }
    }
    const otherParams = new Map<string, string>();
    otherParams.set('selectproperties', `'Title,EditorOwsUser,Description,Path,PictureThumbnailURL,Created,FirstPublishedDate,LastModifiedTime,ModifiedById,AuthorOWSUSER,${this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty},${this.environment.featureSettings.managedPropertyConfig.newsLanguageManagedProperty},${this.environment.featureSettings.managedPropertyConfig.newsIsTopNewsManagedProperty},${this.environment.featureSettings.managedPropertyConfig.newsSenderTagsManagedProperty}'`);
    return this.executeRecursiveSPSearch(queryText, 500, 0, otherParams, false).pipe(
      shareReplay({ bufferSize: 1, refCount: true }));
  }

  /** Search for other (not pages, news, documents or people) */
  getOther(query: string, rowLimit?: number, fileTypesToExclude?: string[], includeOneDriveFiles?: boolean, selectProperties?: string[], hubSiteId?: string, includeNewsSiteContentIfHubSiteQuery?: boolean): Observable<Array<SearchResult>> {

    let hubSiteQuery = '';
    if (hubSiteId) {
      if (includeNewsSiteContentIfHubSiteQuery === true) {
        hubSiteQuery = ` AND ( DepartmentId:{${hubSiteId}} OR Path:${this.featureSettingsStoreService.state.newsCenterSiteUrl}) `;
      } else {
        hubSiteQuery = ` AND DepartmentId:{${hubSiteId}} `;
      }
    }

    const pagesQuery = this.escapeQueryTextForSharePoint(query)
      + ' AND (NOT ((contentclass:STS_List AND ContentTypeId:0x0101009D1CB255DA76424F860D91F20E6C4118*) OR (ContentTypeId:0x010100C568DB52D9D0A14D9B2FDCC96666E9F2007948130EC3DB064584E219954237AF39*) OR (ProgID:OneNote.Notebook) OR (FileType:onetoc2)))'
      + hubSiteQuery;

    const otherParams = new Map<string, string>();
    if (fileTypesToExclude && fileTypesToExclude.length > 0) {

      const multipleFileTypes = fileTypesToExclude.length > 1 ? 'and' : '';
      otherParams.set('refinementfilters', `'${multipleFileTypes}(${fileTypesToExclude.map(fileType => `fileExtension: not(${fileType})`)})'`);
    }

    if (selectProperties) {
      otherParams.set('selectproperties', `'${selectProperties.join(',')}'`);
    }

    return this.executeSearch(pagesQuery, rowLimit, includeOneDriveFiles, otherParams);
  }

  getPromotedResults(query: string, rowLimit: number, hubSiteId?: string, includeNewsSiteContentIfHubSiteQuery?: boolean): Observable<Array<SearchResult>> {

    let hubSiteQuery = '';
    if (hubSiteId) {
      if (includeNewsSiteContentIfHubSiteQuery === true) {
        hubSiteQuery = ` AND ( DepartmentId:{${hubSiteId}} OR Path:${this.featureSettingsStoreService.state.newsCenterSiteUrl}) `;
      } else {
        hubSiteQuery = ` AND DepartmentId:{${hubSiteId}} `;
      }
    }

    const promotedQuery = this.escapeQueryTextForSharePoint(query) + hubSiteQuery;
    let httpParams = new HttpParams()
      .append('querytext', `'${promotedQuery}'`)
      .append('trimduplicates', 'false')
      .append('bypassresulttypes', 'true')
      .append('clienttype', `'custom'`)
      .append('properties', `'EnableDynamicGroups:true'`) // this property enables us to include results from private O365 groups
      .append('enablesorting', 'true');

    if (rowLimit) {
      httpParams = httpParams.append('rowLimit', encodeURI(rowLimit.toString()));
    }
    return this.licenseService.hasUserAny365License().pipe(
      switchMap(isUserLicensed =>
        iif(() => isUserLicensed,
          this.http.get<any>(`${this.environment.coreWorkplaceSiteUrl}/_api/search/query`, {
            params: httpParams,
          })
            .pipe(
              map(data => {
                console.log(`fetched promoted results by search: `);
                console.log(data);
                const results = data as SharePointJsonSearchResult;
                let resultsToReturn = new Array<SearchResult>();
                results.SecondaryQueryResults.forEach(r => {
                  if (r.SpecialTermResults && r.SpecialTermResults.Results) {
                    const tmp = r.SpecialTermResults.Results.map(promotedResult => {
                      return new SearchResult({
                        Title: promotedResult.Title,
                        Path: promotedResult.Url
                      });
                    });
                    resultsToReturn = resultsToReturn.concat(tmp);
                  }
                });
                return resultsToReturn;
              }),
              catchError(this.httpHelper.handleError({ operation: 'getPromotedResults', result: new Array<SearchResult>() }))),
          of([])
        ))
    );
  }

  getBookMarksFromGraph(query: string, rowLimit: number) {

    const otherParams = new Map<string, string>();
    otherParams.set('selectproperties', '_BookMark_');

    return this.executeSearch(query, rowLimit, false, otherParams, true, true);
  }

  /* Sends a post search query to SP search api */
  sendPostSearchQuery(postQuery: SearchPostQuery): Observable<Array<SearchResult>> {

    const httpHeaders = new HttpHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    });
    const options = {
      headers: httpHeaders
    };

    return this.http.post<any>(`${this.environment.coreWorkplaceSiteUrl}/_api/search/postquery`, postQuery, options)
      .pipe(
        map(data => {
          const results = data as SharePointJsonSearchResult;
          if (results && results.PrimaryQueryResult) {
            const resultsToReturn = results.PrimaryQueryResult.RelevantResults.Table.Rows.map(row => {
              const searchResults = {};
              row.Cells.forEach(c => {
                if (c.ValueType.endsWith('Double') || c.ValueType.endsWith('Int32') || c.ValueType.endsWith('Int64')) {
                  searchResults[c.Key] = Number(c.Value);
                } else if (c.ValueType.endsWith('DateTime')) {
                  searchResults[c.Key] = parseISO(c.Value);
                } else {
                  searchResults[c.Key] = c.Value;
                }
              });
              return new SearchResult(searchResults);
            });
            return resultsToReturn;
          } else {
            return new Array<SearchResult>();
          }
        }),
        catchError(this.httpHelper.handleError({ operation: 'sendPostSearchQuery', result: new Array<SearchResult>() })));
  }

  public executeSearch(query: string, rowLimit?: number, includeOneDriveFiles?: boolean, otherParams?: Map<string, string>, trimDuplicates?: boolean, msSearch = false): Observable<Array<SearchResult>> {
    // Note: we are responsible here to format otherParams correctly so it can be appended to search query, for example: &sourceid='b09a7990-05ea-4af9-81ef-edfab16c4e31'
    return this.licenseService.hasUserAny365License().pipe(
      switchMap(isUserLicensed =>
        defer(() =>
        isUserLicensed ? !msSearch ?
                         this.executeSPSearch(query, rowLimit, includeOneDriveFiles, otherParams, trimDuplicates) :
                         this.executeMSSearch(query, rowLimit, includeOneDriveFiles, otherParams, trimDuplicates)
                       : of([])
      )));
  }

  /* sends search queries to microsoft graph*/
  private executeMSSearch(query: string, rowLimit?: number, includeOneDriveFiles?: boolean, otherParams?: Map<string, string>, trimDuplicates?: boolean): Observable<Array<SearchResult>> {

   const fields = otherParams.get('selectproperties').substring(1, otherParams.get('selectproperties').length - 1).split(',');
   let request;
   if (fields.includes('FileExtension')) {
    request = {
      requests: [
        {
          entityTypes: [
            'driveItem'
          ],
          query: {
            queryString: query
          },
          from: 0,
          size: rowLimit,
        }
      ]
    };
   } else if (fields.includes('BookMark')) {
    request = {
       requests: [
       {
        'entityTypes': [
            'bookmark'
          ],
        'query': {
            'queryString': query
          }
        }
     ]
    };
   } else {
    request = {
      requests: [
        {
          entityTypes: [
            'listItem'
          ],
          query: {
            queryString: query
          },
          from: 0,
          size: rowLimit,
          fields: fields
        }
      ]
    };

   }
   const searchResponse = request;

   return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/search/query`, searchResponse)
      .pipe(
        map(data => {
          const results = data.value[0].hitsContainers[0].hits ? data.value[0].hitsContainers[0].hits : [];
          const resultsToReturn: SearchResult[] = results.map(item => {
            if (item.resource.fields) {
              const searchResult = new SearchResult(item.resource.fields);
              searchResult.Title = item.resource.fields.title;
              searchResult.Path = item.resource.fields.path;
              searchResult.Description = item.resource.fields.description;
              searchResult.LastModifiedTime = item.resource.fields.lastModifiedTime;
              searchResult.SiteTitle = item.resource.fields.siteTitle;
              searchResult.FirstPublishedDate =  item.resource.fields.firstPublishedDate;
              searchResult.ModifiedBy = item.resource.lastModifiedBy?.user.displayName;

              return searchResult;
            } else {
              // document search results
              const searchResult =  new SearchResult(item.resource);
              searchResult.Title =   item.resource.name ? item.resource.name.split('.')[0] :  item.resource.displayName;
              searchResult.FileExtension = item.resource.name?.split('.')[1];
              searchResult.Path =  item.resource.webUrl + '?web=1';
              searchResult.LastModifiedTime = item.resource.lastModifiedDateTime;
              searchResult.ModifiedBy = item.resource.lastModifiedBy?.user.displayName;
              searchResult.Description = item.resource.description ? item.resource.description : '';

              return searchResult;
            }

          });
          return resultsToReturn;
        }),
        catchError(this.httpHelper.handleError({ operation: 'executeMSSearch', result: new Array<SearchResult>() })));
  }

  /* Sends search queries to our api */
  private executeSPSearch(query: string, rowLimit?: number, includeOneDriveFiles?: boolean, otherParams?: Map<string, string>, trimDuplicates?: boolean): Observable<Array<SearchResult>> {

    let httpParams = new HttpParams()
      .append('querytext', `'${query}'`)
      .append('trimduplicates', `${trimDuplicates ? 'true' : 'false'}`)
      .append('bypassresulttypes', 'true')
      .append('clienttype', `'custom'`)
      .append('enablesorting', 'true');

    if (rowLimit) {
      httpParams = httpParams.append('rowLimit', encodeURI(rowLimit.toString()));
    }

    if (includeOneDriveFiles) {
      httpParams = httpParams.append('properties', `'EnableDynamicGroups:true,ContentSetting:3'`);
    } else {
      httpParams = httpParams.append('properties', `'EnableDynamicGroups:true'`); // this property enables us to include results from private O365 groups
    }

    if (otherParams) {
      otherParams.forEach((val, key) => {
        httpParams = httpParams.append(key, val);
      });
    }
    return this.http.get<any>(`${this.environment.coreWorkplaceSiteUrl}/_api/search/query`, {
      params: httpParams,
    })
      .pipe(
        map(data => {
          const results = data as SharePointJsonSearchResult;
          let listOfOneDriveResults = new Array<SearchResult>();

          const pageImpresssion = results.Properties.find(val => val.Key === 'piPageImpression');
          const sourceId = results.Properties.find(val => val.Key === 'SourceId');
          const piPageImpressionBlockType = results.PrimaryQueryResult && results.PrimaryQueryResult.RelevantResults.Properties.find(val => val.Key === 'piPageImpressionBlockType');
          let resultsToReturn = results.PrimaryQueryResult.RelevantResults.Table.Rows.map(row => {
            const tmpObj = {
              piPageImpression: pageImpresssion.Value,
              piPageImpressionBlockType: piPageImpressionBlockType.Value,
              SourceId: sourceId.Value,
              SearchTerm: query
            };
            row.Cells.forEach(c => {
              if (c.ValueType.endsWith('Double') || c.ValueType.endsWith('Int32') || c.ValueType.endsWith('Int64')) {
                tmpObj[c.Key] = Number(c.Value);
              } else if (c.ValueType.endsWith('DateTime')) {
                tmpObj[c.Key] = parseISO(c.Value);
              } else {
                tmpObj[c.Key] = c.Value;
              }
            });
            return new SearchResult(tmpObj);
          });

          if (includeOneDriveFiles) {
            results.SecondaryQueryResults.forEach(r => {
              const tmpResults = r.RelevantResults.Table.Rows.map(row => {
                const tmpObj = {};
                row.Cells.forEach(c => {
                  if (c.ValueType.endsWith('Double') || c.ValueType.endsWith('Int32') || c.ValueType.endsWith('Int64')) {
                    tmpObj[c.Key] = Number(c.Value);
                  } else if (c.ValueType.endsWith('DateTime')) {
                    tmpObj[c.Key] = parseISO(c.Value);
                  } else {
                    tmpObj[c.Key] = c.Value;
                  }
                });
                return new SearchResult(tmpObj);
              });
              listOfOneDriveResults = listOfOneDriveResults.concat(tmpResults);
            });

            resultsToReturn = resultsToReturn.concat(listOfOneDriveResults).sort(((a, b) => a.Rank - b.Rank)).slice(0, rowLimit);
          }

          return resultsToReturn;
        }),
        catchError(this.httpHelper.handleError({ operation: 'executeSPSearch', result: new Array<SearchResult>() })));
  }

  private executeRecursiveSPSearch(query: string, rowLimit: number, startRow: number = 0, otherParams?: Map<string, string>, trimDuplicates?: boolean): Observable<Array<SearchResult>> {

    let httpParams = new HttpParams()
      .append('querytext', `'${query}'`)
      .append('trimduplicates', `${trimDuplicates ? 'true' : 'false'}`)
      .append('bypassresulttypes', 'true')
      .append('clienttype', `'custom'`)
      .append('enablesorting', 'true')
      .append('properties', `'EnableDynamicGroups:true'`) // this property enables us to include results from private O365 groups
      .append('rowLimit', encodeURI(rowLimit.toString()))
      .append('startRow', encodeURI(startRow.toString()));

    if (otherParams) {
      otherParams.forEach((val, key) => {
        httpParams = httpParams.append(key, val);
      });
    }

    return this.http.get<any>(`${this.environment.coreWorkplaceSiteUrl}/_api/search/query`, {
      params: httpParams,
    })
      .pipe(
        expand((results: SharePointJsonSearchResult) => {
          if (results) {
            if (results.PrimaryQueryResult.RelevantResults.TotalRows > startRow + results.PrimaryQueryResult.RelevantResults.RowCount) {
              startRow = startRow + results.PrimaryQueryResult.RelevantResults.RowCount;
              const updatedParams = httpParams.set('startRow', startRow.toString());
              return this.http.get<any>(`${this.environment.coreWorkplaceSiteUrl}/_api/search/query`, { params: updatedParams });
            } else {
              return EMPTY;
            }
          } else {
            return EMPTY;
          }
        }),
        toArray(),
        map((results: SharePointJsonSearchResult[]) => {
          const allResults = new Array<SearchResult>();
          results.forEach(result => {
            result.PrimaryQueryResult.RelevantResults.Table.Rows.forEach(row => {
              const tmpObj = {};
              row.Cells.forEach(c => {
                if (c.ValueType.endsWith('Double') || c.ValueType.endsWith('Int32') || c.ValueType.endsWith('Int64')) {
                  tmpObj[c.Key] = Number(c.Value);
                } else if (c.ValueType.endsWith('DateTime')) {
                  tmpObj[c.Key] = parseISO(c.Value);
                } else {
                  tmpObj[c.Key] = c.Value;
                }
              });
              allResults.push(new SearchResult(tmpObj));
            });
          });
          return allResults;
        }),
        catchError(this.httpHelper.handleError({ operation: 'executeRecursiveSPSearch', result: new Array<SearchResult>() })));
  }

  private escapeQueryTextForSharePoint(query: string) {
    // Remove ' , the rest is taken care of by angular
    query = query.replace(/'/, '');
    return query;
  }

}

/*
Result Source 	                        ID
Documents 	                            e7ec8cee-ded8-43c9-beb5-436b54b31e84        Microsoft Office documents and PDF documents
Items matching a content type 	        5dc9f503-801e-4ced-8a2c-5d1237132419        Items that match a content type that the incoming query specifies
Items matching a tag 	                  e1327b9c-2b8c-4b23-99c9-3730cb29c3f7        Documents or list items that match a managed metadata term that the incoming query specifies
Items related to current user 	        48fec42e-4a92-48ce-8363-c2703a40e67d        Documents or list items that are related to the user in a way that the query template specifies
Items with same keyword as this item 	  5c069288-1d17-454a-8ac6-9c642a065f48
Local People Results 	                  b09a7990-05ea-4af9-81ef-edfab16c4e31        People items from the profile database of the User Profile service application
Local Reports And Data Results 	        203fba36-2763-4060-9931-911ac8c0583b        Excel, Office Data Connection (ODC), or Report Definition Language (RDL) items, or items in a report library
Local SharePoint Results 	              8413cd39-2156-4e00-b54d-11efd9abdb89        All items from the local SharePoint search index except People items
Local Video Results 	                  78b793ce-7956-4669-aa3b-451fc5defebf
Pages 	                                5e34578e-4d08-4edc-8bf3-002acf3cdbcc
Pictures 	                              38403c8c-3975-41a8-826e-717f2d41568a
Popular 	                              97c71db1-58ce-4891-8b64-585bc2326c12        Documents and list items sorted by view count
Recently changed items 	                ba63bbae-fa9c-42c0-b027-9a878f16557c        Documents and list items sorted by Modified date
Recommended Items 	                    ec675252-14fa-4fbe-84dd-8d098ed74181        Documents and list items that you recommend for the incoming query
Wiki 	                                  9479bf85-e257-4318-b5a8-81a180f5faa1
    */

/*
 * contentclass:"STS_Site"
"STS_Web": // Site
"STS_List_850": // Page Library
"STS_ListItem_850": // Page
"STS_List_DocumentLibrary": // Document Library
"STS_ListItem_DocumentLibrary": // Document Library Items
"STS_List_GenericList": // Custom List
"STS_ListItem_GenericList": // Custom List Item
"STS_List_Links": // Links List
"STS_ListItem_Links": // Links List Item
"STS_List_Tasks": // Tasks List
"STS_ListItem_Tasks": // Tasks List Item
"STS_List_Events": // Events List
"STS_ListItem_Events": // Events List Item
"STS_List_Announcements": // Announcements List
"STS_ListItem_Announcements": // Announcements List Item
"STS_List_Contacts": // Contacts List
"STS_ListItem_Contacts": // Contacts List Item
"STS_List_DiscussionBoard": // Discussion List
"STS_ListItem_DiscussionBoard": // Discussion List Item
"STS_List_IssueTracking": // Issue Tracking List
"STS_ListItem_IssueTracking": // Issue Tracking List Item
"STS_List_GanttTasks": // Project Tasks List
"STS_ListItem_GanttTasks": // Project Tasks List Item
"STS_List_Survey": // Survey List
"STS_ListItem_Survey": // Survey List Item
"STS_List_PictureLibrary": // Picture Library
"STS_ListItem_PictureLibrary": // Picture Library Item
"STS_List_WebPageLibrary": // Web Page Library
"STS_ListItem_WebPageLibrary": // Web Page Library Item
"STS_List_XMLForm": // Form Library
"STS_ListItem_XMLForm": // Form Library Item
*/
